import React, { useRef, KeyboardEvent, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { font, spacing, colors } from '@mobi/component-library/Theme/Common'
import { greaterThan } from '@mobi/component-library/Utils/media'

interface VerificationCodeInputProps {
  code: string
  onChange: (value: string) => void
  onSubmit?: (code: string) => void
  isValid?: boolean
  isDisabled?: boolean
}

const CODE_LENGTH = 6

export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  code,
  onChange,
  onSubmit,
  isValid = true,
  isDisabled = false,
}) => {
  const [currentInputIndex, setCurrentInputIndex] = useState<number>(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    const inputRef = inputRefs.current[currentInputIndex]
    if (!inputRef) return

    inputRef.focus()
    inputRef.setSelectionRange(-1, -1)
  }, [currentInputIndex])

  const focusNextInput = (focusLeft = false) => {
    const nextIndex = currentInputIndex + (focusLeft ? -1 : 1)
    if (0 <= nextIndex && nextIndex <= CODE_LENGTH - 1 && inputRefs.current[nextIndex])
      setCurrentInputIndex(nextIndex)
  }

  const setInputBoxValue = (index: number, value: string) => {
    // Should only be recieving single digit numbers or empty string
    if (value.length > 1 || (value != ' ' && isNaN(parseInt(value)))) return

    const newCode = [...code]
    newCode[index] = value
    onChange(newCode.join(''))
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    const keyPressed = e.key
    // Paste handled by onPaste
    if (keyPressed === 'v' && (e.metaKey || e.ctrlKey)) return

    e.preventDefault()

    if (keyPressed === 'Backspace') {
      const currentDigit = code[index] || ' '

      if(currentDigit !== ' ') {
        setInputBoxValue(index, ' ')
      } else if (index > 0) {
        setInputBoxValue(index - 1, ' ')
        focusNextInput(true)
      }
    }
    if (keyPressed === 'ArrowLeft' && index > 0) {
      focusNextInput(true)
    }
    if (keyPressed === 'ArrowRight' && index < CODE_LENGTH - 1) {
      focusNextInput()
    }
    // Numbers only
    if (!isNaN(parseInt(keyPressed))) {
      setInputBoxValue(index, keyPressed)
      focusNextInput()
      if (currentInputIndex == CODE_LENGTH - 1) {
        const newCode = [...code]
        newCode[index] = keyPressed
        if (newCode.length == CODE_LENGTH) {
          onSubmit && onSubmit(newCode.join(''))
        }
      }
    }
  }

  const handlePasteCode = (input: string) => {
    const pastedData = input.slice(0, 6)
    const digits = pastedData.replace(/[^\d]/g, '').split('')
    const newCode = [...digits]
    onChange(newCode.join(''))

    if (newCode.length == CODE_LENGTH) {
      onSubmit && onSubmit(newCode.join(''))
      setCurrentInputIndex(CODE_LENGTH - 1)
      return
    }

    // Focus the next empty input or the last input
    const focusIndex = Math.min(newCode.join('').length, CODE_LENGTH - 1)
    setCurrentInputIndex(focusIndex)
  }

  return (
    <MfaCodeInputContainer data-tid-mfa-code-input-form>
      <InputDisplay>
        {Array.from({ length: CODE_LENGTH }).map((_, index) => {
          const digit = code[index] || ''
          return (
            <DigitBoxStyled
              key={index}
              ref={el => (inputRefs.current[index] = el)}
              name={`one-time-code-${index + 1}`}
              value={digit}
              placeholder='0'
              autoComplete='off'
              onChange={e => {
                // System autofill is handled here it tries to put the whole code into the first box
                // So detect if the full code is there and follow the handlePaste logic
                const input = e.target.value
                if (input.length > 1) handlePasteCode(input)
              }}
              onPaste={e => {
                e.preventDefault()
                const pastedData = e.clipboardData.getData('text/plain')
                handlePasteCode(pastedData)
              }}
              onKeyDown={e => handleKeyDown(index, e)}
              onClick={() => setCurrentInputIndex(index)}
              inputMode='numeric'
              pattern='\d'
              isFilled={digit !== ''}
              isValid={isValid}
              // Autofill will try to input the whole code in the first box
              maxLength={index === 0 && digit === '' ? CODE_LENGTH : 1}
              disabled={isDisabled}
            />
          )
        })}
      </InputDisplay>
    </MfaCodeInputContainer>
  )
}

// ======
// Styles
// ======

type DigitBoxStyledProps = {
  isFilled: boolean
  isValid: boolean
}

const largeScreen = greaterThan('px450')

const MfaCodeInputContainer = styled.div({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'center',

  [largeScreen]: {
    alignItems: 'flex-start',
  },
})

const InputDisplay = styled.div({
  display: 'flex',
  gap: spacing.smx1,
  justifyContent: 'space-between',
  fontFamily: font.family.primary,

  [largeScreen]: {
    justifyContent: 'flex-start',
  },
})

const DigitBoxStyled = styled.input<DigitBoxStyledProps>(
  ({ isFilled, isValid }: DigitBoxStyledProps) => ({
    width: '18px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${!isValid ? colors.error[600] : colors.neutral[400]}`,
    background: `${!isValid ? colors.error[50] : colors.white}`,
    borderRadius: '0.5rem',
    ...font.size.xl2,
    fontFamily: font.family.primary,
    fontWeight: font.weight.regular,
    textAlign: 'center' as const,
    color: `${!isValid ? colors.error[600] : isFilled ? colors.black : colors.neutral[400]}`,
    padding: `${spacing.sm}`,
    caretColor: 'transparent',

    // Add focus styling
    '&:focus': {
      outline: 'none',
      border: `1px solid ${colors.lavender[300]}`,
      boxShadow: `0 0 0 4px ${colors.lavender[50]}`,
    },

    [largeScreen]: {
      padding: `${spacing.smx1} ${spacing.smx2}`,
      width: '35px',
    },
  })
)
